import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const KEY = "rm_consent";

export function getConsent() {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

export function CookieBanner() {
  const { t } = useLang();
  const [visible, setVisible] = useState(() => !getConsent());
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const open = () => {
      const c = getConsent();
      setAnalytics(!!c?.analytics);
      setMarketing(!!c?.marketing);
      setSettings(true);
      setVisible(true);
    };
    window.addEventListener("rm-open-cookie-settings", open);
    return () => window.removeEventListener("rm-open-cookie-settings", open);
  }, []);

  const save = (a, m) => {
    localStorage.setItem(KEY, JSON.stringify({ necessary: true, analytics: a, marketing: m, ts: Date.now() }));
    setVisible(false);
    setSettings(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-white/10 bg-anthracite/95 backdrop-blur-xl"
          data-testid="cookie-banner"
        >
          <div className="mx-auto max-w-7xl px-4 py-5 sm:px-8">
            {!settings ? (
              <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
                <p className="max-w-2xl text-sm text-slate-300" data-testid="cookie-text">{t.cookie.text}</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => save(false, false)} className="border border-white/20 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-white/50" data-testid="cookie-necessary-btn">
                    {t.cookie.necessaryOnly}
                  </button>
                  <button onClick={() => setSettings(true)} className="border border-white/20 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-white/50" data-testid="cookie-settings-btn">
                    {t.cookie.settings}
                  </button>
                  <button onClick={() => save(true, true)} className="bg-royal px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-royal-hover" data-testid="cookie-accept-btn">
                    {t.cookie.acceptAll}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-white">{t.cookie.title}</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <label className="flex items-start gap-3 border border-white/10 p-3" data-testid="cookie-cat-necessary">
                    <input type="checkbox" checked disabled className="mt-1 accent-royal" />
                    <span>
                      <span className="block text-sm font-semibold text-white">{t.cookie.necessary}</span>
                      <span className="block text-xs text-slate-400">{t.cookie.necessaryDesc}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 border border-white/10 p-3" data-testid="cookie-cat-analytics">
                    <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1 accent-royal" data-testid="cookie-analytics-toggle" />
                    <span>
                      <span className="block text-sm font-semibold text-white">{t.cookie.analytics}</span>
                      <span className="block text-xs text-slate-400">{t.cookie.analyticsDesc}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 border border-white/10 p-3" data-testid="cookie-cat-marketing">
                    <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-1 accent-royal" data-testid="cookie-marketing-toggle" />
                    <span>
                      <span className="block text-sm font-semibold text-white">{t.cookie.marketing}</span>
                      <span className="block text-xs text-slate-400">{t.cookie.marketingDesc}</span>
                    </span>
                  </label>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => save(analytics, marketing)} className="bg-royal px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-royal-hover" data-testid="cookie-save-btn">
                    {t.cookie.save}
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
